import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send, Sparkles } from "lucide-react";
import { submitLead } from "@/lib/api";
import { PLAN_TYPES } from "@/lib/site";

export const ContactForm = ({ defaultPlan = "Not Sure", compact = false, quizResult = null, prefillMessage = "" }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    plan_interest: defaultPlan,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (prefillMessage) setForm((f) => ({ ...f, message: prefillMessage }));
  }, [prefillMessage]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await submitLead(quizResult ? { ...form, quiz_result: quizResult } : form);
      setDone(true);
      toast.success("Thanks! Christian will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", plan_interest: defaultPlan, message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="contact-form-success"
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center"
      >
        <h3 className="font-display text-2xl text-emerald-900">We got it. Talk soon.</h3>
        <p className="mt-3 text-sm text-emerald-800/90 leading-relaxed">
          Christian personally reviews every inquiry and will reach out within one business day —
          usually much sooner. In a hurry? Call <strong>850-687-7606</strong>.
        </p>
        <button
          type="button"
          data-testid="contact-form-reset"
          onClick={() => setDone(false)}
          className="mt-5 text-sm font-medium text-emerald-900 underline underline-offset-4"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className={`rounded-2xl border border-slate-200 bg-white ${compact ? "p-6" : "p-8 sm:p-10"}`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-slate-500">
            Full name
          </Label>
          <Input
            id="name"
            data-testid="contact-input-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Jane Beachcomber"
            className="mt-2 h-11"
          />
        </div>
        <div>
          <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-slate-500">
            Phone
          </Label>
          <Input
            id="phone"
            data-testid="contact-input-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(850) 555-0123"
            className="mt-2 h-11"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-slate-500">
            Email
          </Label>
          <Input
            id="email"
            data-testid="contact-input-email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@example.com"
            type="email"
            className="mt-2 h-11"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
            I’m interested in
          </Label>
          <Select
            value={form.plan_interest}
            onValueChange={(v) => update("plan_interest", v)}
          >
            <SelectTrigger
              data-testid="contact-select-plan"
              className="mt-2 h-11"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PLAN_TYPES.map((p) => (
                <SelectItem key={p} value={p} data-testid={`contact-select-option-${p.toLowerCase().replace(/\s+/g, "-")}`}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-slate-500">
            Message <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
          </Label>
          <Textarea
            id="message"
            data-testid="contact-input-message"
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            placeholder="What's prompting your search? Any specifics about doctors, medications, or budget?"
            rows={4}
            className="mt-2"
          />
        </div>
      </div>
      {quizResult && (
        <div
          data-testid="contact-quiz-result"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--bg-sand)] text-[var(--brand-secondary-dark)] text-xs font-semibold px-4 py-2"
        >
          <Sparkles className="w-3.5 h-3.5" /> Quiz result attached: {quizResult}
        </div>
      )}
      <Button
        type="submit"
        data-testid="contact-form-submit"
        disabled={loading}
        className="mt-7 w-full sm:w-auto rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white px-7 h-12"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" /> Send message
          </>
        )}
      </Button>
      <p className="mt-3 text-xs text-slate-500">
        By submitting, you consent to be contacted about insurance options. No spam, ever.
      </p>
    </form>
  );
};
